/**
 * Categorical series palette for the dashboard charts.
 *
 * The hues and their ORDER are not cosmetic - they were validated against the
 * real chart surfaces (light #FFFFFF, dark --ff-surface-card #1C3041) on the
 * lightness band, chroma floor, colour-blind separation and normal-vision
 * separation checks. Keep the order as-is; re-order or re-step only after
 * re-validating.
 *
 * Two rules this module exists to enforce:
 *  1. A series keeps its colour. Colour follows the product, never its position
 *     in the response, so changing a filter never repaints the series that
 *     survive it.
 *  2. Past 8 series colour alone can no longer separate them, so the slots
 *     repeat with a stroke-dash layered on top (composite encoding) instead of
 *     inventing new hues.
 */

export const SERIES_LIGHT = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
];

export const SERIES_DARK = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
];

/** Stroke patterns for each additional lap through the 8 colour slots. */
export const SERIES_DASH = [null, "6 4", "2 3", "10 4 2 4"];

const SLOT_COUNT = SERIES_LIGHT.length;

/*
 * scope -> (series key -> slot). Each chart gets its own eight slots: the
 * products on one card and the employees on another are unrelated sets, and
 * sharing one registry let whichever chart rendered first consume the slots
 * and push the other's series past the eighth - where the colours repeat and
 * the lines come back dashed.
 */
const slotsByScope = new Map();

function registryFor(scope) {
  if (!slotsByScope.has(scope)) {
    slotsByScope.set(scope, new Map());
  }
  return slotsByScope.get(scope);
}

function hashKey(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * Preferred slot comes from the name, so the same product gets the same colour
 * between sessions. If that slot is taken we probe forward, which keeps every
 * visible series distinct for the first 8.
 */
function assignSlot(key, assignedSlots) {
  const taken = new Set(
    Array.from(assignedSlots.values()).map((slot) => slot % SLOT_COUNT)
  );
  const preferred = hashKey(key) % SLOT_COUNT;
  if (taken.size >= SLOT_COUNT) {
    return assignedSlots.size;
  }
  for (let step = 0; step < SLOT_COUNT; step += 1) {
    const candidate = (preferred + step) % SLOT_COUNT;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
  return preferred;
}

function slotFor(key, scope) {
  const assignedSlots = registryFor(scope);
  const name = String(key ?? "");
  if (!assignedSlots.has(name)) {
    assignedSlots.set(name, assignSlot(name, assignedSlots));
  }
  return assignedSlots.get(name);
}

export function getSeriesColor(key, isDark = false, scope = "default") {
  const ramp = isDark ? SERIES_DARK : SERIES_LIGHT;
  return ramp[slotFor(key, scope) % SLOT_COUNT];
}

export function getSeriesDash(key, scope = "default") {
  const lap = Math.floor(slotFor(key, scope) / SLOT_COUNT);
  return SERIES_DASH[lap % SERIES_DASH.length] || undefined;
}

/** Colour map for a list of series, in one pass. */
export function buildSeriesColors(keys = [], isDark = false, scope = "default") {
  return keys.reduce((map, key) => {
    map[key] = getSeriesColor(key, isDark, scope);
    return map;
  }, {});
}

